import { PLACES_TYPES_VALUE_PLACE } from "@raiden/library/constants/places";
import { usePreferences } from "@raiden/library/contexts/Preferences";
import { useApi } from "@raiden/library/hooks/useApi";
import { serverApiFetcher } from "@raiden/library/hooks/useApiFetcher";
import generateApiUri from "@raiden/library/libraries/utils/generateApiUri";
import { useIntl } from "react-intl";
import Seo from "../../components/Seo";
import PrestationsSearchPublic from "../../containers/Prestations/SearchPublic";

/**
 * @param {object} params
 * @param {number} params.environmentId
 */
function getPrestationCategoriesUrl({ environmentId }) {
  return generateApiUri({
    id: "@prestations.categories.viewAny",
    query: {
      environment_id: [environmentId],
      per_page: 250,
    },
  });
}

/**
 * @param {object} params
 * @param {number} params.environmentId
 */
function getPlacesUrl({ environmentId }) {
  return generateApiUri({
    id: "@places.viewAny",
    query: {
      environment_id: [environmentId],
      type: [PLACES_TYPES_VALUE_PLACE],
      with_public_prestations: true,
      per_page: 250,
    },
  });
}

/**
 * @typedef {object} PageProps
 * @property {String} layout
 * @property {import("@raiden/library/types/Api/ApiResponse").ApiResponse<import("@raiden/library/types/PrestationCategory").PrestationCategory[]>} [prestationCategoriesResponse]
 * @property {import("@raiden/library/types/Api/ApiResponse").ApiResponse<import("@raiden/library/types/Place").Place[]>} [placesResponse]
 */

/**
 * @param {PageProps} props
 */
function Page({ prestationCategoriesResponse, placesResponse }) {
  const intl = useIntl();

  const { bestEnvironment } = usePreferences();

  /** @type {import("@raiden/library/hooks/useApi").UseApi<import("@raiden/library/types/PrestationCategory").PrestationCategory[]>} */
  const { swrResponse: swrResponsePrestationCategories } = useApi(
    bestEnvironment
      ? getPrestationCategoriesUrl({ environmentId: bestEnvironment.id })
      : null,
    {
      swrConfig: {
        fallbackData: prestationCategoriesResponse,
      },
    },
  );

  /** @type {import("@raiden/library/hooks/useApi").UseApi<import("@raiden/library/types/Place").Place[]>} */
  const { swrResponse: swrResponsePlaces } = useApi(
    bestEnvironment
      ? getPlacesUrl({ environmentId: bestEnvironment.id })
      : null,
    {
      swrConfig: {
        fallbackData: placesResponse,
      },
    },
  );

  return (
    <>
      <Seo
        title={intl.formatMessage({ defaultMessage: "Activités" })}
        description={intl.formatMessage({
          defaultMessage: "Découvrez les activités proposées par le centre.",
        })}
      />

      <PrestationsSearchPublic
        swrResponsePrestationCategories={swrResponsePrestationCategories}
        swrResponsePlaces={swrResponsePlaces}
      />
    </>
  );
}

export default Page;

/**
 * @param {import("../../types/AppContext").PageContext} context
 *
 * @returns {Promise<import("../../types/Page").PageInitialProps & PageProps>}
 */
Page.getInitialProps = async function (context) {
  /** @type {import("../../types/Page").PageInitialProps & PageProps} */
  const values = {
    layout: "public",
  };

  if (typeof window === "undefined") {
    const bestEnvironment =
      context.configuration?.environments?.length === 1
        ? context.configuration.environments[0]
        : undefined;

    if (bestEnvironment) {
      values.prestationCategoriesResponse = await serverApiFetcher(
        getPrestationCategoriesUrl({ environmentId: bestEnvironment.id }),
      ).catch(() => {});

      values.placesResponse = await serverApiFetcher(
        getPlacesUrl({ environmentId: bestEnvironment.id }),
      ).catch(() => {});
    }
  }

  return values;
};
